import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Field, Input, MessageBar, MessageBarBody, Text, makeStyles, tokens } from "@fluentui/react-components";
import { forbiddenWords } from "@shared/data";
import { registrationSchema } from "@shared/schemas";

import Wizard from "../../../shared/components/Wizard";
import CountryCombobox from "../../../shared/components/CountryCombobox";
import DataValidationMessageBar from "../../../shared/components/DataValidationMessageBar";
import { useNotify } from "../../../shared/components/SuperToast";
import { useItemValidation } from "../../../shared/hooks/useItemValidation";
import { useRegisterIdentity } from "../hooks/useIdentityMutations";
import { useTranslation } from "react-i18next";

const turnstileSiteKey = (process.env.REACT_APP_TURNSTILE_SITE_KEY || "").trim();
const turnstileIframeBaseUrl = (process.env.REACT_APP_TURNSTILE_IFRAME_URL || "").trim();
const mainDomain = (process.env.REACT_APP_MAIN_DOMAIN || "").trim();
const appProtocol = (process.env.REACT_APP_PROTOCOL || "https").trim().replace(/:\/\/$/, "");

const useStyles = makeStyles({
  pageGrid: {
    display: "grid",
    gap: "12px",
  },
  twoColumns: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "12px",
    "@media (max-width: 640px)": {
      gridTemplateColumns: "1fr",
    },
  },
  helper: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
  summaryBox: {
    display: "grid",
    gap: "8px",
    padding: "12px",
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground2,
  },
  summarySectionTitle: {
    fontWeight: tokens.fontWeightSemibold,
    marginTop: "4px",
  },
  summaryLine: {
    color: tokens.colorNeutralForeground2,
    fontSize: tokens.fontSizeBase200,
  },
  captchaHint: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
  captchaError: {
    color: tokens.colorPaletteRedForeground1,
    fontSize: tokens.fontSizeBase200,
  },
});

const initialData = {
  email: "",
  password: "",
  phone: "",
  tenant: {
    name: "",
    slug: "",
  },
  site: {
    code: "",
    name: "",
    address: "",
    zipcode: "",
    city: "",
    state: "",
    country_code: "",
    latitude: "",
    longitude: "",
  },
  user: {
    fname: "",
    lname: "",
    name: "",
    title: "",
    mobile: "",
  },
};

const slugify = (value) =>
  (value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");

const forbiddenSlugSet = new Set((forbiddenWords?.forbidden || []).map((word) => String(word).toLowerCase()));

const applyForbiddenSlugValidation = (errors, slug) => {
  if (!slug) return errors;
  if (!forbiddenSlugSet.has(String(slug).toLowerCase())) return errors;

  return {
    ...errors,
    slug: "This subdomain is reserved. Please choose another slug.",
  };
};

const normalizeCoordinate = (value) => {
  const strValue = String(value ?? "").trim();
  if (!strValue) return null;

  const parsed = Number(strValue);
  return Number.isFinite(parsed) ? parsed : null;
};

const partialCoordinateRegex = /^-?\d*(\.\d*)?$/;
const decimalCoordinateRegex = /^-?\d+(\.\d+)?$/;

const validateCoordinateField = (rawValue, label, min, max) => {
  const strValue = String(rawValue ?? "").trim();
  if (!strValue) return null;

  if (!decimalCoordinateRegex.test(strValue)) {
    return `${label} must be a decimal number (e.g. 48.8566).`;
  }

  const parsed = Number(strValue);
  if (!Number.isFinite(parsed) || parsed < min || parsed > max) {
    return `${label} must be between ${min} and ${max}.`;
  }

  return null;
};

const applySiteCoordinateValidation = (errors, site) => {
  const nextErrors = { ...errors };
  const latitudeError = validateCoordinateField(site?.latitude, "Latitude", -90, 90);
  const longitudeError = validateCoordinateField(site?.longitude, "Longitude", -180, 180);

  if (latitudeError) nextErrors.latitude = latitudeError;
  else delete nextErrors.latitude;

  if (longitudeError) nextErrors.longitude = longitudeError;
  else delete nextErrors.longitude;

  return nextErrors;
};

export default function Register() {
  const styles = useStyles();
  const navigate = useNavigate();
  const { notify } = useNotify();
  const { t } = useTranslation();

  const [isOpen, setIsOpen] = useState(true);
  const [data, setData] = useState(initialData);
  const [validationErrors, setValidationErrors] = useState({});
  const [isLocating, setIsLocating] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState("");
  const [isTurnstileReady, setIsTurnstileReady] = useState(!turnstileSiteKey);
  const [turnstileError, setTurnstileError] = useState("");
  const hasValidationErrors = Object.keys(validationErrors).length > 0;

  const handleCancel = () => {
    if (mainDomain) {
      window.location.href = `${appProtocol}://${mainDomain}`;
      return;
    }

    window.history.back();
  };

  const turnstileIframeUrl = (() => {
    if (!turnstileSiteKey) return "";
    try {
      const url = new URL(turnstileIframeBaseUrl, window.location.origin);
      url.searchParams.set("sitekey", turnstileSiteKey);
      url.searchParams.set("parentOrigin", window.location.origin);
      return url.toString();
    } catch {
      return "";
    }
  })();

  useEffect(() => {
    if (!turnstileSiteKey) return;
    if (!turnstileIframeUrl) {
      setIsTurnstileReady(false);
      setTurnstileError("CAPTCHA iframe URL is invalid.");
      return;
    }

    const expectedOrigin = new URL(turnstileIframeUrl).origin;

    setIsTurnstileReady(false);
    setTurnstileToken("");
    setTurnstileError("");

    const onMessage = (event) => {
      if (event.origin !== expectedOrigin) return;

      const payload = event.data;
      if (!payload || payload.source !== "itsm-turnstile") return;

      if (payload.type === "ready") {
        setIsTurnstileReady(true);
        setTurnstileError("");
        return;
      }

      if (payload.type === "token") {
        setIsTurnstileReady(true);
        setTurnstileToken(payload.token || "");
        setTurnstileError("");
        return;
      }

      if (payload.type === "expired") {
        setTurnstileToken("");
        return;
      }

      if (payload.type === "error") {
        setTurnstileToken("");
        setIsTurnstileReady(false);
        setTurnstileError(
          "CAPTCHA failed to load. Check ad-blockers, DNS filtering, or site key domain settings." +
            (payload.errorCode ? ` (code: ${payload.errorCode})` : ""),
        );
      }
    };

    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [turnstileIframeUrl]);

  const summaryValue = (value) => {
    const str = String(value ?? "").trim();
    return str || "-";
  };

  const maskedPassword = (password) => {
    const length = String(password || "").length;
    return length > 0 ? "*".repeat(Math.min(length, 16)) : "-";
  };

  const registrationDef = registrationSchema.$defs.registration;

  const validateAccount = useItemValidation({
    type: "object",
    properties: {
      email: registrationDef.properties.email,
      password: registrationDef.properties.password,
      phone: registrationDef.properties.phone,
    },
    required: ["email", "password", "phone"],
  });
  const validateTenant = useItemValidation(registrationDef.properties.tenant);
  const validateSite = useItemValidation(registrationDef.properties.site);
  const validateUser = useItemValidation(registrationDef.properties.user);
  const validateSiteWithCoordinates = (site) => applySiteCoordinateValidation(validateSite(site), site);

  const registerMutation = useRegisterIdentity(
    (response) => {
      notify(response?.message || "Registration completed successfully.", "success");
      navigate("/login", { replace: true });
    },
    (error) => {
      notify(error?.message || "Registration failed.", "error");
    },
    true,
  );

  const pages = [
    {
      title: t("generic.account"),
      description: t("registration.accountProvideInfo"),
      render: ({ data, updateData }) => (
        <div className={styles.pageGrid}>
          <Field label={t("generic.email")} required validationState={validationErrors.email ? "error" : undefined}>
            <Input
              type="email"
              aria-invalid={!!validationErrors.email}
              value={data.email}
              onChange={(_, d) => {
                const updated = { email: d.value, password: data.password, phone: data.phone };
                updateData({ email: d.value });
                setValidationErrors(validateAccount(updated));
              }}
              placeholder="you@example.com"
            />
          </Field>
          <Field
            label={t("generic.password")}
            required
            validationState={validationErrors.password ? "error" : undefined}
          >
            <Input
              type="password"
              aria-invalid={!!validationErrors.password}
              value={data.password}
              onChange={(_, d) => {
                const updated = { email: data.email, password: d.value, phone: data.phone };
                updateData({ password: d.value });
                setValidationErrors(validateAccount(updated));
              }}
            />
          </Field>
          <Field label={t("generic.phone")} required validationState={validationErrors.phone ? "error" : undefined}>
            <Input
              aria-invalid={!!validationErrors.phone}
              value={data.phone}
              onChange={(_, d) => {
                const updated = { email: data.email, password: data.password, phone: d.value };
                updateData({ phone: d.value });
                setValidationErrors(validateAccount(updated));
              }}
            />
          </Field>
          {hasValidationErrors && <DataValidationMessageBar validationErrors={validationErrors} />}
        </div>
      ),
      validate: (d) => {
        const errors = validateAccount({ email: d.email, password: d.password, phone: d.phone });
        setValidationErrors(errors);
        if (Object.keys(errors).length > 0) return "Please fix highlighted account fields.";
        return true;
      },
      onNext: ({ data, updateData }) => {
        setValidationErrors({});
        updateData((prev) => ({
          ...prev,
          email: data.email.trim(),
          phone: data.phone.trim(),
          user: {
            ...prev.user,
            mobile: prev.user.mobile || data.phone.trim(),
          },
        }));
      },
    },
    {
      title: t("generic.tenant"),
      description: t("registration.tenantProvideInfo"),
      render: ({ data, updateData }) => (
        <div className={styles.pageGrid}>
          <Field label={t("generic.name")} required validationState={validationErrors.name ? "error" : undefined}>
            <Input
              aria-invalid={!!validationErrors.name}
              value={data.tenant.name}
              onChange={(_, d) => {
                const nextName = d.value;
                const nextTenant = {
                  ...data.tenant,
                  name: nextName,
                  slug: data.tenant.slug ? data.tenant.slug : slugify(nextName),
                };
                updateData((prev) => ({
                  ...prev,
                  tenant: nextTenant,
                }));
                setValidationErrors(applyForbiddenSlugValidation(validateTenant(nextTenant), nextTenant.slug));
              }}
              placeholder="My Company"
            />
          </Field>
          <Field label={t("generic.subdomain")} required validationState={validationErrors.slug ? "error" : undefined}>
            <Input
              aria-invalid={!!validationErrors.slug}
              value={data.tenant.slug}
              onChange={(_, d) => {
                const nextTenant = { ...data.tenant, slug: slugify(d.value) };
                updateData((prev) => ({
                  ...prev,
                  tenant: nextTenant,
                }));
                setValidationErrors(applyForbiddenSlugValidation(validateTenant(nextTenant), nextTenant.slug));
              }}
              placeholder="mycompany"
            />
          </Field>
          <div className={styles.helper}>URL preview: https://{data.tenant.slug || "your-tenant"}.easyitsm.app</div>
          {hasValidationErrors && <DataValidationMessageBar validationErrors={validationErrors} />}
        </div>
      ),
      validate: (d) => {
        const errors = applyForbiddenSlugValidation(validateTenant(d.tenant), d.tenant?.slug);
        setValidationErrors(errors);
        if (Object.keys(errors).length > 0) return "Please fix highlighted tenant fields.";
        return true;
      },
      onNext: ({ data, updateData }) => {
        setValidationErrors({});
        updateData((prev) => ({
          ...prev,
          tenant: {
            ...prev.tenant,
            name: data.tenant.name.trim(),
            slug: slugify(data.tenant.slug),
          },
        }));
      },
    },
    {
      title: t("generic.site"),
      description: t("registration.siteProvideInfo"),
      render: ({ data, updateData }) => (
        <div className={styles.pageGrid}>
          <div style={{ display: "flex", gap: "12px" }}>
            <Field label="Code" validationState={validationErrors.code ? "error" : undefined} style={{ width: "84px" }}>
              <Input
                aria-invalid={!!validationErrors.code}
                value={data.site.code}
                maxLength={3}
                style={{ width: "60px" }}
                onChange={(_, d) => {
                  const nextSite = { ...data.site, code: d.value };
                  updateData((prev) => ({ ...prev, site: nextSite }));
                  setValidationErrors(validateSiteWithCoordinates(nextSite));
                }}
              />
            </Field>
            <Field
              label={t("generic.name")}
              required
              validationState={validationErrors.name ? "error" : undefined}
              style={{ flex: 1, minWidth: 0 }}
            >
              <Input
                aria-invalid={!!validationErrors.name}
                value={data.site.name}
                onChange={(_, d) => {
                  const nextSite = { ...data.site, name: d.value };
                  updateData((prev) => ({ ...prev, site: nextSite }));
                  setValidationErrors(validateSiteWithCoordinates(nextSite));
                }}
              />
            </Field>
          </div>
          <Field label={t("generic.address")} required validationState={validationErrors.address ? "error" : undefined}>
            <Input
              aria-invalid={!!validationErrors.address}
              value={data.site.address}
              onChange={(_, d) => {
                const nextSite = { ...data.site, address: d.value };
                updateData((prev) => ({ ...prev, site: nextSite }));
                setValidationErrors(validateSiteWithCoordinates(nextSite));
              }}
            />
          </Field>
          <div className={styles.twoColumns}>
            <Field
              label={t("generic.zipcode")}
              required
              validationState={validationErrors.zipcode ? "error" : undefined}
            >
              <Input
                aria-invalid={!!validationErrors.zipcode}
                value={data.site.zipcode}
                onChange={(_, d) => {
                  const nextSite = { ...data.site, zipcode: d.value };
                  updateData((prev) => ({ ...prev, site: nextSite }));
                  setValidationErrors(validateSiteWithCoordinates(nextSite));
                }}
              />
            </Field>
            <Field label={t("generic.city")} required validationState={validationErrors.city ? "error" : undefined}>
              <Input
                aria-invalid={!!validationErrors.city}
                value={data.site.city}
                onChange={(_, d) => {
                  const nextSite = { ...data.site, city: d.value };
                  updateData((prev) => ({ ...prev, site: nextSite }));
                  setValidationErrors(validateSiteWithCoordinates(nextSite));
                }}
              />
            </Field>
          </div>
          <div className={styles.twoColumns}>
            <Field label={t("generic.state")} required validationState={validationErrors.state ? "error" : undefined}>
              <Input
                aria-invalid={!!validationErrors.state}
                value={data.site.state}
                onChange={(_, d) => {
                  const nextSite = { ...data.site, state: d.value };
                  updateData((prev) => ({ ...prev, site: nextSite }));
                  setValidationErrors(validateSiteWithCoordinates(nextSite));
                }}
              />
            </Field>
            <Field
              label={t("generic.country")}
              required
              validationState={validationErrors.country_code ? "error" : undefined}
            >
              <CountryCombobox
                value={data.site?.country_code ?? ""}
                onChange={(code) => {
                  const nextSite = { ...data.site, country_code: code };
                  updateData((prev) => ({ ...prev, site: nextSite }));
                  setValidationErrors(validateSiteWithCoordinates(nextSite));
                }}
              />
            </Field>
          </div>
          <div className={styles.twoColumns}>
            <Field label={t("generic.latitude")} validationState={validationErrors.latitude ? "error" : undefined}>
              <Input
                aria-invalid={!!validationErrors.latitude}
                value={String(data.site.latitude ?? "")}
                inputMode="decimal"
                onChange={(_, d) => {
                  if (d.value && !partialCoordinateRegex.test(d.value)) return;
                  const nextSite = { ...data.site, latitude: d.value };
                  updateData((prev) => ({ ...prev, site: nextSite }));
                  setValidationErrors(validateSiteWithCoordinates(nextSite));
                }}
              />
            </Field>
            <Field label={t("generic.longitude")} validationState={validationErrors.longitude ? "error" : undefined}>
              <Input
                aria-invalid={!!validationErrors.longitude}
                value={String(data.site.longitude ?? "")}
                inputMode="decimal"
                onChange={(_, d) => {
                  if (d.value && !partialCoordinateRegex.test(d.value)) return;
                  const nextSite = { ...data.site, longitude: d.value };
                  updateData((prev) => ({ ...prev, site: nextSite }));
                  setValidationErrors(validateSiteWithCoordinates(nextSite));
                }}
              />
            </Field>
          </div>
          <Button
            appearance="subtle"
            disabled={isLocating}
            style={{ justifySelf: "start" }}
            onClick={() => {
              if (!navigator.geolocation) {
                notify("Geolocation is not supported by your browser.", "error");
                return;
              }

              setIsLocating(true);
              navigator.geolocation.getCurrentPosition(
                ({ coords }) => {
                  const nextSite = {
                    ...data.site,
                    latitude: String(coords.latitude),
                    longitude: String(coords.longitude),
                  };
                  updateData((prev) => ({ ...prev, site: nextSite }));
                  setValidationErrors(validateSiteWithCoordinates(nextSite));
                  setIsLocating(false);
                },
                (error) => {
                  const messageByCode = {
                    1: "Location permission denied.",
                    2: "Location is unavailable.",
                    3: "Location request timed out.",
                  };
                  notify(messageByCode[error.code] || "Unable to retrieve location.", "error");
                  setIsLocating(false);
                },
                { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 },
              );
            }}
          >
            {isLocating ? `${t("nounPhrases.gettingLocation")}...` : t("nounPhrases.getGeolocation")}
          </Button>
          {hasValidationErrors && <DataValidationMessageBar validationErrors={validationErrors} />}
        </div>
      ),
      validate: (d) => {
        const errors = validateSiteWithCoordinates(d.site);
        setValidationErrors(errors);
        if (Object.keys(errors).length > 0) return "Please fix highlighted site fields.";
        return true;
      },
      onNext: ({ data, updateData }) => {
        setValidationErrors({});
        updateData((prev) => ({
          ...prev,
          site: {
            ...prev.site,
            code: (data.site.code || "").trim().toUpperCase(),
            name: data.site.name.trim(),
            address: data.site.address.trim(),
            zipcode: data.site.zipcode.trim(),
            city: data.site.city.trim(),
            state: data.site.state.trim(),
            country_code: String(data.site.country_code || "").toLowerCase(),
            latitude: normalizeCoordinate(data.site.latitude),
            longitude: normalizeCoordinate(data.site.longitude),
          },
        }));
      },
    },
    {
      title: t("generic.user"),
      description: t("registration.profileProvideInfo"),
      render: ({ data, updateData }) => (
        <div className={styles.pageGrid}>
          <div className={styles.twoColumns}>
            <Field label={t("generic.fname")} required validationState={validationErrors.fname ? "error" : undefined}>
              <Input
                aria-invalid={!!validationErrors.fname}
                value={data.user.fname}
                onChange={(_, d) => {
                  const nextUser = { ...data.user, fname: d.value };
                  updateData((prev) => ({ ...prev, user: nextUser }));
                  setValidationErrors(validateUser(nextUser));
                }}
              />
            </Field>
            <Field label={t("generic.lname")} required validationState={validationErrors.lname ? "error" : undefined}>
              <Input
                aria-invalid={!!validationErrors.lname}
                value={data.user.lname}
                onChange={(_, d) => {
                  const nextUser = { ...data.user, lname: d.value };
                  updateData((prev) => ({ ...prev, user: nextUser }));
                  setValidationErrors(validateUser(nextUser));
                }}
              />
            </Field>
          </div>
          <Field label={t("generic.name")} validationState={validationErrors.name ? "error" : undefined}>
            <Input
              aria-invalid={!!validationErrors.name}
              value={data.user.name}
              onChange={(_, d) => {
                const nextUser = { ...data.user, name: d.value };
                updateData((prev) => ({ ...prev, user: nextUser }));
                setValidationErrors(validateUser(nextUser));
              }}
            />
          </Field>
          <div className={styles.twoColumns}>
            <Field label={t("generic.title")} required validationState={validationErrors.title ? "error" : undefined}>
              <Input
                aria-invalid={!!validationErrors.title}
                value={data.user.title}
                onChange={(_, d) => {
                  const nextUser = { ...data.user, title: d.value };
                  updateData((prev) => ({ ...prev, user: nextUser }));
                  setValidationErrors(validateUser(nextUser));
                }}
              />
            </Field>
            <Field label={t("generic.mobile")} required validationState={validationErrors.mobile ? "error" : undefined}>
              <Input
                aria-invalid={!!validationErrors.mobile}
                value={data.user.mobile}
                onChange={(_, d) => {
                  const nextUser = { ...data.user, mobile: d.value };
                  updateData((prev) => ({ ...prev, user: nextUser }));
                  setValidationErrors(validateUser(nextUser));
                }}
              />
            </Field>
          </div>
          {registerMutation.error ? (
            <MessageBar intent="error">
              <MessageBarBody>{registerMutation.error?.message || "An error occurred"}</MessageBarBody>
            </MessageBar>
          ) : null}
          {hasValidationErrors && <DataValidationMessageBar validationErrors={validationErrors} />}
        </div>
      ),
      validate: (d) => {
        const errors = validateUser(d.user);
        setValidationErrors(errors);
        if (Object.keys(errors).length > 0) return "Please fix highlighted user fields.";
        return true;
      },
      onNext: async ({ data, updateData }) => {
        setValidationErrors({});
        const normalizedName = data.user.name?.trim() || `${data.user.fname} ${data.user.lname}`.trim();
        updateData((prev) => ({
          ...prev,
          user: {
            ...prev.user,
            fname: data.user.fname.trim(),
            lname: data.user.lname.trim(),
            name: normalizedName,
            title: data.user.title.trim(),
            mobile: data.user.mobile.trim(),
          },
        }));
      },
    },
    {
      title: t("generic.summary"),
      description: t("registration.confirmationInfo"),
      finishLabel: registerMutation.isPending ? "Sending..." : "Register",
      render: ({ data }) => (
        <div className={styles.pageGrid}>
          <div className={styles.summaryBox}>
            <Text className={styles.summarySectionTitle}>{t("generic.account")}</Text>
            <Text className={styles.summaryLine}>
              {t("generic.email")}: {summaryValue(data.email)}
            </Text>
            <Text className={styles.summaryLine}>
              {t("generic.password")}: {maskedPassword(data.password)}
            </Text>
            <Text className={styles.summaryLine}>
              {t("generic.phone")}: {summaryValue(data.phone)}
            </Text>

            <Text className={styles.summarySectionTitle}>{t("generic.tenant")}</Text>
            <Text className={styles.summaryLine}>
              {t("generic.name")}: {summaryValue(data.tenant?.name)}
            </Text>
            <Text className={styles.summaryLine}>
              {t("generic.subdomain")}: {summaryValue(data.tenant?.slug)}
            </Text>

            <Text className={styles.summarySectionTitle}>{t("generic.site")}</Text>
            <Text className={styles.summaryLine}>
              {t("generic.code")}: {summaryValue(data.site?.code)}
            </Text>
            <Text className={styles.summaryLine}>
              {t("generic.name")}: {summaryValue(data.site?.name)}
            </Text>
            <Text className={styles.summaryLine}>
              {t("generic.address")}: {summaryValue(data.site?.address)}
            </Text>
            <Text className={styles.summaryLine}>
              {t("generic.zipcode")}: {summaryValue(data.site?.zipcode)}
            </Text>
            <Text className={styles.summaryLine}>
              {t("generic.city")}: {summaryValue(data.site?.city)}
            </Text>
            <Text className={styles.summaryLine}>
              {t("generic.state")}: {summaryValue(data.site?.state)}
            </Text>
            <Text className={styles.summaryLine}>
              {t("generic.country")}: {summaryValue(data.site?.country_code)}
            </Text>
            <Text className={styles.summaryLine}>
              {t("generic.latitude")}: {summaryValue(data.site?.latitude)}
            </Text>
            <Text className={styles.summaryLine}>
              {t("generic.longitude")}: {summaryValue(data.site?.longitude)}
            </Text>

            <Text className={styles.summarySectionTitle}>{t("generic.user")}</Text>
            <Text className={styles.summaryLine}>
              {t("generic.fname")}: {summaryValue(data.user?.fname)}
            </Text>
            <Text className={styles.summaryLine}>
              {t("generic.lname")}: {summaryValue(data.user?.lname)}
            </Text>
            <Text className={styles.summaryLine}>
              {t("generic.name")}: {summaryValue(data.user?.name)}
            </Text>
            <Text className={styles.summaryLine}>
              {t("generic.title")}: {summaryValue(data.user?.title)}
            </Text>
            <Text className={styles.summaryLine}>
              {t("generic.mobile")}: {summaryValue(data.user?.mobile)}
            </Text>
          </div>

          {turnstileSiteKey && (
            <div>
              <iframe
                title="Cloudflare Turnstile"
                src={turnstileIframeUrl}
                style={{ width: "100%", height: "72px", border: "none" }}
                scrolling="no"
              />
              {!isTurnstileReady && <Text className={styles.captchaHint}>Loading CAPTCHA...</Text>}
              {!!turnstileError && <Text className={styles.captchaError}>{turnstileError}</Text>}
              {isTurnstileReady && !turnstileToken && !turnstileError && (
                <Text className={styles.captchaError}>Please complete the CAPTCHA.</Text>
              )}
            </div>
          )}

          {registerMutation.error ? (
            <MessageBar intent="error">
              <MessageBarBody>{registerMutation.error?.message || "An error occurred"}</MessageBarBody>
            </MessageBar>
          ) : null}
        </div>
      ),
      validate: () => {
        if (turnstileSiteKey && !turnstileToken) {
          return "Please complete the CAPTCHA.";
        }
        return true;
      },
    },
  ];

  const handleFinish = async (formData) => {
    const payload = {
      email: formData.email.trim(),
      password: formData.password,
      phone: formData.phone.trim(),
      turnstileToken,
      tenant: {
        name: formData.tenant.name.trim(),
        slug: slugify(formData.tenant.slug),
      },
      site: {
        code: (formData.site.code || "").trim().toUpperCase(),
        name: formData.site.name.trim(),
        address: formData.site.address.trim(),
        zipcode: formData.site.zipcode.trim(),
        city: formData.site.city.trim(),
        state: formData.site.state.trim(),
        country_code: String(formData.site.country_code || "").toLowerCase(),
        latitude: normalizeCoordinate(formData.site.latitude),
        longitude: normalizeCoordinate(formData.site.longitude),
      },
      user: {
        fname: formData.user.fname.trim(),
        lname: formData.user.lname.trim(),
        name: (formData.user.name || `${formData.user.fname} ${formData.user.lname}`).trim(),
        title: formData.user.title.trim(),
        mobile: formData.user.mobile.trim(),
      },
    };

    await registerMutation.mutateAsync(payload);
  };

  return (
    <Wizard
      title={t("generic.registration")}
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      data={data}
      setData={setData}
      pages={pages}
      nextLabel={t("generic.next")}
      backLabel={t("generic.back")}
      cancelLabel={t("generic.cancel")}
      finishLabel={registerMutation.isPending ? t("generic.sending") : t("generic.register")}
      onCancel={handleCancel}
      onFinish={handleFinish}
    />
  );
}
